import { useCallback, useEffect, useRef, useState } from "react";
import api, { errMsg, fileUrl } from "../lib/api";
import { toast } from "sonner";

export const pdfWorking = (status) => ["queued", "rendering", "retry"].includes(status);
export const pdfStatusLabel = (status) => ({ queued: "Queued", rendering: "Generating…", retry: "Retrying…", ready: "Ready", failed: "Failed", missing: "Not generated" }[status] || status);

export const useProposalPdfs = (quoteId, revision, enabled = true, idPrefix = "", token = null) => {
  const [exports, setExports] = useState([]);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState("");
  const requesting = useRef(false);
  const load = useCallback(async () => {
    if (!quoteId) return;
    const { data } = await api.get(`/quotes/${quoteId}/proposal-pdfs`);
    setExports(data); setError("");
  }, [quoteId]);
  useEffect(() => {
    if (!enabled || !quoteId) return;
    setExports([]);
    load().catch(() => setError("Could not load proposal PDF status."));
  }, [load, revision, enabled, quoteId]);

  // Poll faster (2s) while a PDF is actively generating so "Ready" appears sooner,
  // and back off (8s) when idle. This only changes how quickly the UI notices a
  // finished PDF — the server-side renderer is untouched.
  const anyWorking = exports.some((x) => pdfWorking(x.status));
  useEffect(() => {
    if (!enabled || !quoteId) return;
    const timer = setInterval(() => load().catch(() => setError("Could not refresh PDF status.")), anyWorking ? 2000 : 8000);
    return () => clearInterval(timer);
  }, [load, enabled, quoteId, anyWorking]);

  const act = async (item) => {
    if (item.status === "ready" && item.file_id) {
      window.open(`${fileUrl(item.file_id)}?download=1`, "_blank", "noopener,noreferrer");
      return;
    }
    if (requesting.current || pdfWorking(item.status)) return;
    requesting.current = true; setBusy(item.scope);
    try {
      const { data } = await api.post(`/quotes/${quoteId}/proposal-pdfs`, { scope: item.scope });
      setExports((prev) => prev.map((entry) => entry.scope === item.scope ? data : entry));
      toast.success(data.status === "ready" ? "Proposal PDF is ready to download" : "PDF queued — it will be saved in Documents");
    } catch (e) { toast.error(errMsg(e)); }
    finally { requesting.current = false; setBusy(null); }
  };

  // Direct render → open the inline PDF straight in a new tab, without saving to Documents/Drive.
  // The backend serves it as inline application/pdf, so the browser renders it natively — no blob juggling.
  const openPreview = (renderScope) => {
    if (!token) return;
    const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
    window.open(`${API}/public/quote/${token}/pdf?scope=${renderScope}&kind=proposal`, "_blank", "noopener,noreferrer");
  };

  const items = exports.map((item, index) => ({
    id: `${idPrefix}${index === 0 ? "proposal-pdf-all" : `proposal-pdf-option-${index - 1}`}`,
    icon: item.status === "ready" ? "Download" : pdfWorking(item.status) ? "Clock3" : "FilePlus2",
    label: `${item.label} — ${item.status === "ready" ? "Download proposal PDF" : pdfWorking(item.status) ? pdfStatusLabel(item.status) : item.status === "failed" ? "Retry proposal PDF" : "Generate proposal PDF"}`,
    disabled: busy !== null || pdfWorking(item.status), onSelect: () => act(item),
  }));

  // "Open PDF (preview)" per option + all options — direct, no Documents write.
  if (token) {
    exports.forEach((item, index) => {
      const renderScope = index === 0 ? "all" : String(index - 1);
      items.push({
        id: `${idPrefix}proposal-pdf-preview-${index === 0 ? "all" : `option-${index - 1}`}`,
        icon: "FileSearch",
        label: `${item.label} — Open PDF (preview)`,
        onSelect: () => openPreview(renderScope),
      });
    });
  }
  if (error || !exports.length) items.push({ id: `${idPrefix}proposal-pdf-refresh`, icon: "RefreshCw", label: error ? "Retry loading proposal PDFs" : "Loading proposal PDFs…", disabled: !error, onSelect: () => load().catch(() => toast.error("Could not load proposal PDFs")) });
  return { items, exports, error, working: exports.filter((x) => pdfWorking(x.status)).length };
};